import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, Card, CardContent, Typography, useTheme } from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import rocket_green from "../../../../assets/images/rocket_green.svg";
import useFetch from "../../../../hooks/useFetch";
import * as kApi from "../../../../constants/api";

// Componentes
import PackageItem from "./PackageItem";
import RecurrenceOptions from "./RecurrenceOptions";
import PriceDisplay from "./PriceDisplay";
import ActionButtons from "./ActionButtons";

// Utilitários
import { calculatePrices } from "./priceUtils";
import SystemContext from "../../../../context/SystemContext";
import { useContext } from "react";
/**
 * Componente principal MyAddPurchase
 *
 * Responsável por exibir a tela de compra do plano, permitindo ao usuário
 * escolher entre pagamento mensal ou anual, com cálculo dinâmico de preços.
 *
 * @param {string} props.PlanValueMonthly - Valor do plano mensal
 * @param {string} props.PlanValueAnnual - Valor do plano anual
 */
const MyAddPurchase = ({ PlanValueMonthly, PlanValueAnnual }) => {
  // Hooks e estados
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const [recurrence, setRecurrence] = useState("Annual");
  const [planName, setPlanName] = useState("");
  const payload = useSelector((state) => state.purchase);
  const { changePropertyUrl } = useContext(SystemContext);
  const [products, isLoadingProducts, hasErrorProducts, fetchProducts] = useFetch({
      autoInit: false,
    });
  // Cálculo de preços memoizado para evitar recálculos desnecessários
  const priceInfo = useMemo(() => {
    return calculatePrices(PlanValueMonthly, PlanValueAnnual);
  }, [PlanValueMonthly, PlanValueAnnual]);

  // Handlers para navegação
  const goToInvoiceData = () => {
    changePropertyUrl( (recurrence === "Monthly" ? "mensal" : "anual"))
    dispatch({
      type: "PURCHASE",
      purchase: {
        content: "PaymentArea",
        step: "InvoiceData",
        errorList: [],
      },
    });
  };

  const backToAddType = () => {
    changePropertyUrl(-2)
    dispatch({
      type: "PURCHASE",
      purchase: {
        content: "ChooseAdd",
        step: "SelectAddType",
      },
    });
  };

  // Efeitos
  useEffect(() => {
    fetchProducts({
      url: kApi.API_URL_PRODUCTS,
      method: "GET",
    });
  }, [fetchProducts]);

  // Atualiza estado no Redux quando a recorrência muda
  useEffect(() => {
    dispatch({
      type: "PURCHASE",
      PostData: {
        periodicity: recurrence === "Monthly" ? "monthly" : "annual",
      },
      planInfo: {
        value: recurrence === "Monthly" ? PlanValueMonthly : PlanValueAnnual,
        periodicity: recurrence === "Monthly" ? "monthly" : "annual",
      },
    });
  }, [recurrence, PlanValueMonthly, PlanValueAnnual, dispatch]);

  // Atualiza o nome do plano quando os produtos são carregados
  useEffect(() => {
    const getNameById = (id_product) => {
      const foundItem = products?.periodicity?.find(
        (item) => item.id_product === id_product
      );
      return foundItem ? foundItem.name : null;
    };

    if (payload?.PostData?.product_id && products?.periodicity) {
      const name = getNameById(payload.PostData.product_id[0]);
      setPlanName(name);

      dispatch({
        type: "PURCHASE",
        planInfo: {
          planName: name,
        },
      });
    }
  }, [products, dispatch, payload?.PostData?.product_id]);

  // Renderização do componente
  return (
    <Card
      sx={{
        borderRadius: "8.121px",
        margin: "auto",
        width: "537.256px",
        display: "flex",
        flexDirection: "column",
        [theme.breakpoints.down("md")]: {
          marginTop: "2vh",
        },
        [theme.breakpoints.down("sm")]: {
          width: "100%",
        },
      }}
    >
      {/* Cabeçalho com ícone do carrinho */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          fontSize: "17.032px",
          color: "#4d4d4d",
          textTransform: "uppercase",
          fontWeight: 700,
          border: "1px solid #dddbdb",
          width: "100%",
          height: "80px",
        }}
      >
        <ShoppingCartIcon
          sx={{ marginInline: "22px 16px", color: "#662d91" }}
        />
        <Typography sx={{ fontWeight: 700 }}>
          {t("purchase.my_purchase")}
        </Typography>
      </Box>

      {/* Conteúdo do cartão */}
      <CardContent
        sx={{
          borderRadius: "8.121px",
          margin: "auto",
          padding: "0 35px 35px 35px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          [theme.breakpoints.down("sm")]: {
            padding: "0 15px 15px 15px",
          },
        }}
      >
        {/* Título da seção de itens adicionados */}
        <Typography
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-start",
            color: "#474747",
            fontSize: "15.086px",
            fontWeight: 500,
            marginBlockStart: "20px",
          }}
        >
          {t("purchase.added_items")}
        </Typography>

        {/* Item do pacote selecionado */}
        {
          (planName && PlanValueMonthly && PlanValueAnnual) && (<PackageItem
            planName={planName}
            monthlyValue={PlanValueMonthly}
            annualValue={PlanValueAnnual}
            t={t}
            theme={theme}
          />)
        }


        {/* Título da seção de recorrência */}
        <Typography
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-start",
            color: "#474747",
            fontSize: "15.086px",
            fontWeight: 500,
            marginBlockStart: "20px",
          }}
        >
          {t("purchase.select_recurrence")}
        </Typography>

        {/* Opções de recorrência (mensal/anual) */}
        <RecurrenceOptions
          recurrence={recurrence}
          setRecurrence={setRecurrence}
          t={t}
        />

        {/* Seção de preços */}
        <PriceDisplay
          recurrence={recurrence}
          priceInfo={priceInfo}
          rocketIcon={rocket_green}
        />

        {/* Botões de ação */}
        <ActionButtons
          onContinue={goToInvoiceData}
          onReturn={backToAddType}
          t={t}
        />
      </CardContent>
    </Card>
  );
};

export default MyAddPurchase;
